import React, { useState, useEffect } from 'react';
// Link no longer used in footer after switching to external anchors
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { motion } from 'framer-motion';
import { trackUsage, startDwellTimer } from '../lib/telemetry';
import { RootState } from '../app/store';
import { updateUserProfile, signOut } from '../app/slices/authSlice';
import { addToast } from '../app/slices/uiSlice';
import {
  openCustomerPortal,
  refreshEntitlements,
  cancelTrialAtEnd,
  cancelTrialNow,
  Plan,
} from '../app/slices/subscriptionSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import PaywallModal from '../components/PaywallModal';
import Footer from '../components/Footer';
import {
  UserCircleIcon,
  CogIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ArrowRightOnRectangleIcon,
  CreditCardIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { PROVINCES, DistanceUnit } from '../types/models';
import { isValidBN, isValidGSTAcct, formatBN, formatGSTAcct } from '../lib/validators';
import { updateUserDistanceUnit } from '../lib/profile';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import { useEntitlements } from '../app/hooks/useEntitlements';

// dev-only alias provided in env.ts; safe to import

const TaxSettingsSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  const [taxSettings, setTaxSettings] = useState<{
    filingFrequency: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
    preferredPeriodStart: string;
    preferredPeriodEnd: string;
    taxYearLabel?: string;
  } | null>(null);

  const reportPeriod = {
    start: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Jan 1
    end: new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0], // Dec 31
  };

  // Load tax settings
  useEffect(() => {
    (async () => {
      if (!user?.uid) return;
      const ref = doc(db, 'users', user.uid, 'settings', 'tax');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() as any;
        setTaxSettings({
          filingFrequency: data.filingFrequency || 'QUARTERLY',
          preferredPeriodStart: data.preferredPeriodStart || reportPeriod.start,
          preferredPeriodEnd: data.preferredPeriodEnd || reportPeriod.end,
          taxYearLabel: data.taxYearLabel,
        });
      }
    })();
  }, [user?.uid]);

  useEffect(() => {
    // Track page view and dwell time
    trackUsage('usage_page_view', { page: 'Profile' }).catch(console.warn);
    const stop = startDwellTimer('Profile');
    return stop;
  }, []);

  // REMOVED: Populate subscription state from user billing data
  // This was causing issues because subscriptionSlice already handles this
  // in its extraReducers when auth/initialize/fulfilled is dispatched.
  // Having it here was creating a race condition where the plan would be FREE
  // until the user visited the Profile page.

  const saveTaxSettings = async () => {
    if (!user?.uid || !taxSettings) return;
    const ref = doc(db, 'users', user.uid, 'settings', 'tax');
    await setDoc(
      ref,
      {
        ...taxSettings,
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
    dispatch(addToast({ message: 'Tax settings saved', type: 'success' }));
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label className="label">Filing Frequency</label>
        <select
          className="input"
          value={taxSettings?.filingFrequency || 'QUARTERLY'}
          onChange={e =>
            setTaxSettings(prev => ({
              ...(prev || {
                preferredPeriodStart: reportPeriod.start,
                preferredPeriodEnd: reportPeriod.end,
              }),
              filingFrequency: e.target.value as any,
            }))
          }
        >
          <option value="MONTHLY">Monthly</option>
          <option value="QUARTERLY">Quarterly</option>
          <option value="ANNUAL">Annual</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Filing Frequency controls how we group your GST/HST return periods
          (Monthly/Quarterly/Annual).
        </p>
      </div>
      <div>
        <label className="label">Tax Year Label (optional)</label>
        <input
          type="text"
          className="input"
          placeholder="e.g., 2025"
          value={taxSettings?.taxYearLabel || ''}
          onChange={e =>
            setTaxSettings(prev => ({
              ...(prev || {
                preferredPeriodStart: reportPeriod.start,
                preferredPeriodEnd: reportPeriod.end,
                filingFrequency: 'QUARTERLY',
              }),
              taxYearLabel: e.target.value,
            }))
          }
        />
      </div>
      <div>
        <label className="label">Preferred Tax Period Start</label>
        <input
          type="date"
          className="input"
          value={taxSettings?.preferredPeriodStart || reportPeriod.start}
          onChange={e =>
            setTaxSettings(prev => ({
              ...(prev || {
                filingFrequency: 'QUARTERLY',
                preferredPeriodEnd: reportPeriod.end,
              }),
              preferredPeriodStart: e.target.value,
            }))
          }
        />
      </div>
      <div>
        <label className="label">Preferred Tax Period End</label>
        <input
          type="date"
          className="input"
          value={taxSettings?.preferredPeriodEnd || reportPeriod.end}
          onChange={e =>
            setTaxSettings(prev => ({
              ...(prev || {
                filingFrequency: 'QUARTERLY',
                preferredPeriodStart: reportPeriod.start,
              }),
              preferredPeriodEnd: e.target.value,
            }))
          }
        />
      </div>
      <div className="sm:col-span-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Preferred Tax Period is the date range for your next report. You can still add past
            expenses and sessions.
          </p>
          <button className="btn-primary btn-sm" onClick={saveTaxSettings}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user, isLoading } = useSelector((state: RootState) => state.auth);
  const {
    plan,
    status,
    archiveEndsAt,
    isLoading: subscriptionLoading,
  } = useSelector((state: RootState) => state.subscription);
  const { isAdmin } = useEntitlements();

  const [isEditing, setIsEditing] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [preselectedPlanForPaywall, setPreselectedPlanForPaywall] = useState<
    'monthly' | 'annual' | undefined
  >(undefined);

  // Log when Profile component mounts
  React.useEffect(() => {
    console.log('[Profile] Component mounted', {
      plan,
      status,
      subscriptionLoading,
      userId: user?.uid,
    });
  }, []);

  // Log whenever plan changes
  React.useEffect(() => {
    console.log('[Profile] Plan state changed:', {
      plan,
      status,
      subscriptionLoading,
      archiveEndsAt,
    });
  }, [plan, status, subscriptionLoading, archiveEndsAt]);

  // Handle URL parameters for checkout success
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('archive_success') === 'true') {
      console.log('[Profile] Archive success detected, refreshing entitlements');
      trackUsage('usage_checkout_complete', { plan: 'archive', status: 'success' }).catch(
        console.warn
      );
      dispatch(refreshEntitlements());
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      dispatch(addToast({ message: 'Archive plan activated successfully!', type: 'success' }));
    } else if (urlParams.get('success') === 'true') {
      console.log('[Profile] Checkout success detected, refreshing entitlements');
      // Optional: confirm a Payment Link or Checkout Session without webhooks
      const sessionId = urlParams.get('session_id');
      if (sessionId) {
        console.log('[Profile] Confirming checkout session:', sessionId);
        // lazy import to avoid circular dep at top
        import('../app/slices/subscriptionSlice').then(({ confirmCheckout }) => {
          dispatch(confirmCheckout({ sessionId }))
            .unwrap()
            .then(() => {
              console.log('[Profile] Checkout confirmed, refreshing entitlements');
              dispatch(refreshEntitlements());
            })
            .catch(() => {
              /* ignore; toast below still shows success */
            });
        });
      }
      trackUsage('usage_checkout_complete', { plan: 'pro', status: 'success' }).catch(console.warn);
      dispatch(refreshEntitlements());
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      dispatch(addToast({ message: 'Pro plan activated successfully!', type: 'success' }));
    }
  }, [dispatch]);
  const [formData, setFormData] = useState({
    displayName: user?.displayName || '',
    fullLegalName: user?.fullLegalName || '',
    province: user?.province || 'BC',
    businessNumber: user?.businessNumber || '',
    gstHstAccount: user?.gstHstAccount || '',
    gstRegistered: user?.gstRegistered || false,
    distanceUnit: (user?.distanceUnit || 'km') as DistanceUnit,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.businessNumber && !isValidBN(formData.businessNumber)) {
      newErrors.businessNumber = 'Business Number must be 9 digits';
    }

    if (formData.gstHstAccount && !isValidGSTAcct(formData.gstHstAccount)) {
      newErrors.gstHstAccount = 'GST/HST Account format: 123456789RT0001';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!user?.uid || !validateForm()) return;

    try {
      await dispatch(updateUserProfile(formData));

      // Also update distance unit in Firestore
      await updateUserDistanceUnit(user.uid, formData.distanceUnit);

      dispatch(
        addToast({
          message: 'Profile updated successfully',
          type: 'success',
        })
      );
      setIsEditing(false);
    } catch {
      dispatch(
        addToast({
          message: 'Failed to update profile',
          type: 'error',
        })
      );
    }
  };

  const handleSignOut = () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      dispatch(signOut());
    }
  };

  const handleManageBilling = async () => {
    try {
      const url = await dispatch(openCustomerPortal()).unwrap();
      window.open(url, '_blank');
    } catch {
      dispatch(
        addToast({
          message: 'Failed to open billing portal. Please try again.',
          type: 'error',
        })
      );
    }
  };

  const handleCancelTrial = async () => {
    if (
      !window.confirm(
        'Are you sure you want to cancel your trial? You will lose Pro access immediately.'
      )
    ) {
      return;
    }

    try {
      await dispatch(cancelTrialNow()).unwrap();
      dispatch(
        addToast({
          message: 'Trial cancelled successfully',
          type: 'success',
        })
      );
      await dispatch(refreshEntitlements());
    } catch (error: any) {
      dispatch(
        addToast({
          message: error.message || 'Failed to cancel trial',
          type: 'error',
        })
      );
    }
  };

  const handleUpgradeToAnnual = () => {
    // Open paywall modal with Annual preselected
    setPreselectedPlanForPaywall('annual');
    setShowPaywallModal(true);
  };

  const handleGoPro = () => {
    setPreselectedPlanForPaywall(undefined);
    setShowPaywallModal(true);
  };

  const handleExportData = async () => {
    if (!user?.uid) return;

    trackUsage('usage_export_start').catch(console.warn);
    try {
      const beginAccountExportFn = httpsCallable<
        {},
        { success: boolean; downloadUrl: string; expiresAt: string }
      >(functions, 'beginAccountExport');
      const result = await beginAccountExportFn();

      if (result.data?.downloadUrl) {
        // Open download in new tab
        window.open(result.data.downloadUrl, '_blank');
        trackUsage('usage_export_finish', { status: 'success' }).catch(console.warn);
        dispatch(
          addToast({
            message: 'Data export started. Download will begin shortly.',
            type: 'success',
          })
        );
      } else {
        throw new Error('No download URL received');
      }
    } catch (error: any) {
      trackUsage('usage_export_finish', { status: 'fail', error: error.message }).catch(
        console.warn
      );
      dispatch(addToast({ message: 'Failed to export data. Please try again.', type: 'error' }));
    }
  };

  const handleCancelTrialAtEnd = async () => {
    trackUsage('usage_trial_cancel_click', { mode: 'at_trial_end' }).catch(console.warn);

    if (
      !window.confirm(
        "Cancel your trial at the end of the current trial period? You'll keep Pro features until then."
      )
    ) {
      return;
    }

    try {
      const result = await dispatch(cancelTrialAtEnd()).unwrap();

      if (result.code === 'LATE_CANCEL') {
        dispatch(
          addToast({
            message:
              "Trial cancellation submitted, but you're within 25 hours of trial end. Stripe may have already generated an invoice.",
            type: 'warning',
          })
        );
      } else {
        dispatch(
          addToast({
            message: 'Trial will be cancelled at the end of your current trial period.',
            type: 'success',
          })
        );
      }
      // Refresh entitlements to update UI
      await dispatch(refreshEntitlements());
    } catch (error: any) {
      dispatch(
        addToast({
          message: error || 'Failed to cancel trial. Please try again.',
          type: 'error',
        })
      );
    }
  };

  const handleCancelTrialNow = async () => {
    trackUsage('usage_trial_cancel_click', { mode: 'now' }).catch(console.warn);

    if (
      !window.confirm(
        "Cancel your trial immediately and downgrade to Free plan? You'll lose Pro features right away."
      )
    ) {
      return;
    }

    try {
      await dispatch(cancelTrialNow()).unwrap();
      dispatch(
        addToast({
          message: 'Trial cancelled immediately. You now have Free plan access.',
          type: 'success',
        })
      );
      // Refresh entitlements to update UI
      await dispatch(refreshEntitlements());
    } catch (error: any) {
      dispatch(
        addToast({
          message: error || 'Failed to cancel trial. Please try again.',
          type: 'error',
        })
      );
    }
  };

  const getPlanDisplayName = (plan: Plan): string => {
    switch (plan) {
      case 'PRO-MONTHLY':
        return 'Pro Monthly';
      case 'PRO-ANNUAL':
        return 'Pro Annual';
      case 'PRO_TRIAL':
        return 'Pro Monthly (Trial)';
      case 'ARCHIVE':
        return 'Archive Only';
      case 'ARCHIVE-ONE-TIME':
        return 'Archive (6-year)';
      default:
        return 'Free';
    }
  };

  const getPlanBadgeColor = (plan: Plan): string => {
    switch (plan) {
      case 'PRO-MONTHLY':
      case 'PRO-ANNUAL':
      case 'PRO_TRIAL':
        return 'bg-green-100 text-green-800';
      case 'ARCHIVE':
      case 'ARCHIVE-ONE-TIME':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Branding Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-5 shadow-md"
      >
        <div className="flex items-center justify-center space-x-3 mb-2">
          <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
            AutoTaxPilot™
          </h1>
        </div>
        <p className="text-center text-sm text-gray-600 font-medium">
          Your Data, Your Control • Top Security • Only You Have Access
        </p>
      </motion.div>

      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-3xl font-bold leading-7 text-gray-900 sm:text-4xl sm:truncate"
          >
            Profile Settings
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-2 text-base text-gray-600"
          >
            Manage your account and tax settings
          </motion.p>
        </div>
      </div>

      {/* Profile Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card"
      >
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
            <button onClick={() => setIsEditing(!isEditing)} className="btn-secondary btn-sm">
              <CogIcon className="h-4 w-4 mr-2" />
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-6 mb-6">
          {user.photoURL ? (
            <img className="h-20 w-20 rounded-full" src={user.photoURL} alt={user.displayName} />
          ) : (
            <div className="h-20 w-20 rounded-full bg-gray-300 flex items-center justify-center">
              <UserCircleIcon className="h-12 w-12 text-gray-600" />
            </div>
          )}
          <div>
            <h4 className="text-xl font-medium text-gray-900">{user.displayName}</h4>
            <p className="text-sm text-gray-500">{user.email}</p>
            <p className="text-sm text-gray-500">
              Member since{' '}
              {new Date(
                typeof (user.createdAt as any).toDate === 'function'
                  ? (user.createdAt as any).toDate()
                  : user.createdAt
              ).toLocaleDateString('en-CA')}
            </p>
            <div className="mt-2 flex items-center gap-2">
              {isAdmin && (
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                  <ShieldCheckIcon className="mr-1 h-3 w-3" />
                  Admin access detected
                </span>
              )}
            </div>
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="label">Display Name</label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={e => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                  className="input"
                />
              </div>

              <div>
                <label className="label">Province/Territory</label>
                <select
                  value={formData.province}
                  onChange={e => setFormData(prev => ({ ...prev, province: e.target.value }))}
                  className="input"
                >
                  {PROVINCES.map(province => (
                    <option key={province.code} value={province.code}>
                      {province.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Distance Unit</label>
                <select
                  value={formData.distanceUnit}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, distanceUnit: e.target.value as DistanceUnit }))
                  }
                  className="input"
                >
                  <option value="km">Kilometres (km)</option>
                  <option value="mi">Miles (mi)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Used across sessions, reports, and placeholders.
                </p>
              </div>
            </div>

            <div>
              <label className="label">Full Legal Name</label>
              <input
                type="text"
                placeholder="e.g., Larson Raymond Rockwell"
                value={formData.fullLegalName}
                onChange={e => setFormData(prev => ({ ...prev, fullLegalName: e.target.value }))}
                className="input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your full legal name as it appears on official documents
              </p>
            </div>

            <div>
              <label className="label">Business Number (BN – 9 digits)</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder="123456789"
                maxLength={9}
                value={formData.businessNumber}
                onChange={e => {
                  const value = e.target.value.replace(/\D/g, ''); // Only digits
                  setFormData(prev => ({ ...prev, businessNumber: value }));
                  if (errors.businessNumber) {
                    setErrors(prev => ({ ...prev, businessNumber: '' }));
                  }
                }}
                className={`input ${errors.businessNumber ? 'border-red-500' : ''}`}
              />
              {errors.businessNumber && (
                <p className="text-xs text-red-600 mt-1">{errors.businessNumber}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Your 9-digit CRA business number if you have one
              </p>
            </div>

            <div>
              <label className="label">GST/HST Account</label>
              <input
                type="text"
                placeholder="123456789RT0001"
                maxLength={15}
                value={formData.gstHstAccount}
                onChange={e => {
                  const value = e.target.value.toUpperCase();
                  setFormData(prev => ({ ...prev, gstHstAccount: value }));
                  if (errors.gstHstAccount) {
                    setErrors(prev => ({ ...prev, gstHstAccount: '' }));
                  }
                }}
                className={`input ${errors.gstHstAccount ? 'border-red-500' : ''}`}
              />
              {errors.gstHstAccount && (
                <p className="text-xs text-red-600 mt-1">{errors.gstHstAccount}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Format: 123456789RT0001 (9 digits + RT + 4 digits)
              </p>
            </div>

            <div className="flex items-center">
              <input
                id="gst-registered"
                type="checkbox"
                checked={formData.gstRegistered}
                onChange={e => setFormData(prev => ({ ...prev, gstRegistered: e.target.checked }))}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="gst-registered" className="ml-2 block text-sm text-gray-900">
                I am registered for GST/HST
              </label>
            </div>

            <div className="flex justify-end space-x-3">
              <button onClick={() => setIsEditing(false)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleSave} className="btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="label">Province/Territory</label>
              <p className="text-sm text-gray-900">
                {PROVINCES.find(p => p.code === user.province)?.name || user.province}
              </p>
            </div>

            <div>
              <label className="label">GST/HST Registration</label>
              <div className="flex items-center">
                {user.gstRegistered ? (
                  <>
                    <ShieldCheckIcon className="h-5 w-5 text-success-500 mr-2" />
                    <span className="text-sm text-success-700">Registered</span>
                  </>
                ) : (
                  <>
                    <span className="h-5 w-5 bg-gray-300 rounded-full mr-2"></span>
                    <span className="text-sm text-gray-500">Not registered</span>
                  </>
                )}
              </div>
            </div>

            {user.fullLegalName && (
              <div>
                <label className="label">Full Legal Name</label>
                <p className="text-sm text-gray-900">{user.fullLegalName}</p>
              </div>
            )}

            {user.businessNumber && (
              <div>
                <label className="label">Business Number</label>
                <p className="text-sm text-gray-900">{formatBN(user.businessNumber)}</p>
              </div>
            )}

            {user.gstHstAccount && (
              <div>
                <label className="label">GST/HST Account</label>
                <p className="text-sm text-gray-900">{formatGSTAcct(user.gstHstAccount)}</p>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Tax Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card"
      >
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Tax Settings</h3>
            <div className="text-sm text-gray-600">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                GST/HST Rate:{' '}
                {(() => {
                  const province = PROVINCES.find(p => p.code === user.province);
                  if (!province) return 'Unknown';

                  if (province.hstRate > 0) {
                    return `${province.hstRate}% HST`;
                  } else {
                    return `${province.gstRate}% GST + ${province.pstRate}% PST`;
                  }
                })()}
              </span>
            </div>
          </div>
        </div>
        <TaxSettingsSection />
      </motion.div>

      {/* Billing */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card"
      >
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Billing & Subscription</h3>
            {subscriptionLoading ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                Loading...
              </span>
            ) : (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(plan)}`}
              >
                {getPlanDisplayName(plan)}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {subscriptionLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" text="Loading subscription..." />
            </div>
          ) : plan === 'FREE' ? (
            <div className="text-center py-6">
              <SparklesIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h4 className="mt-2 text-sm font-medium text-gray-900">Upgrade to Pro</h4>
              <p className="mt-1 text-sm text-gray-500">
                Unlock unlimited expense tracking, automated tax calculations, and priority support.
              </p>
              <div className="mt-4">
                <button onClick={handleGoPro} className="btn-primary">
                  <SparklesIcon className="h-4 w-4 mr-2" />
                  Go Pro - $12/mo or $120/yr
                </button>
              </div>
            </div>
          ) : status === 'trialing' ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-start">
                <SparklesIcon className="h-5 w-5 text-blue-400 mt-0.5" />
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-medium text-blue-800">Free Trial Active</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Trial ends on{' '}
                    {new Date(
                      (user as any)?.billing?.currentPeriodEnd ||
                        Date.now() + 7 * 24 * 60 * 60 * 1000
                    ).toLocaleDateString('en-CA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                  <div className="mt-3 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleCancelTrialAtEnd}
                      disabled={subscriptionLoading}
                      className="btn-primary btn-sm text-xs px-3 py-2"
                    >
                      {subscriptionLoading ? 'Processing...' : 'Cancel at Trial End (No Charge)'}
                    </button>
                    <button
                      onClick={handleCancelTrialNow}
                      disabled={subscriptionLoading}
                      className="btn-outline btn-sm text-xs px-3 py-2"
                    >
                      {subscriptionLoading ? 'Processing...' : 'Cancel Now (Downgrade Immediately)'}
                    </button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Cancel at least 25 hours before trial end to guarantee no charge.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Current Plan</h4>
                  <p className="text-sm text-gray-500">
                    {plan === 'PRO-MONTHLY' && '$12/month'}
                    {plan === 'PRO-ANNUAL' && '$120/year'}
                    {plan === 'PRO_TRIAL' && '$12/month after trial'}
                    {plan === 'ARCHIVE' && '$1.99/month (Read-only access)'}
                    {plan === 'ARCHIVE-ONE-TIME' && 'One-time payment (6-year read-only access)'}
                  </p>
                  {status === 'past_due' && (
                    <p className="text-xs text-red-600 mt-1">
                      Payment past due - please update your billing information
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleManageBilling}
                    disabled={subscriptionLoading}
                    className="btn-secondary btn-sm"
                  >
                    <CreditCardIcon className="h-4 w-4 mr-2" />
                    {subscriptionLoading ? 'Loading...' : 'Manage Billing'}
                  </button>
                  {plan === 'PRO_TRIAL' && (
                    <button
                      onClick={handleCancelTrial}
                      disabled={subscriptionLoading}
                      className="btn-danger btn-sm"
                    >
                      {subscriptionLoading ? 'Cancelling...' : 'Cancel Trial'}
                    </button>
                  )}
                  {plan === 'PRO-MONTHLY' && (
                    <button
                      onClick={handleUpgradeToAnnual}
                      disabled={subscriptionLoading}
                      className="btn-outline btn-sm"
                    >
                      Upgrade to Annual — Save 2 months
                    </button>
                  )}
                </div>
              </div>

              {(plan === 'PRO-MONTHLY' || plan === 'PRO-ANNUAL') && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex">
                    <ShieldCheckIcon className="h-5 w-5 text-green-400" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-green-800">Pro Features Active</h4>
                      <div className="mt-2 text-sm text-green-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Unlimited expense tracking</li>
                          <li>Automated GST/HST calculations</li>
                          <li>Tax report generation</li>
                          <li>Priority support</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(plan === 'ARCHIVE' || plan === 'ARCHIVE-ONE-TIME') && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <DocumentTextIcon className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-yellow-800">
                        {plan === 'ARCHIVE-ONE-TIME' ? 'Archive Plan (6-year)' : 'Archive Plan'}
                      </h4>
                      {plan === 'ARCHIVE-ONE-TIME' && archiveEndsAt && (
                        <p className="text-xs text-yellow-600 mt-1">
                          Expires: {new Date(archiveEndsAt).toLocaleDateString('en-CA')}
                        </p>
                      )}
                      <div className="mt-2 text-sm text-yellow-700">
                        <ul className="list-disc list-inside space-y-1">
                          <li>Read-only access to existing data</li>
                          <li>Cannot add new expenses or sessions</li>
                          <li>Cannot generate new reports</li>
                          <li>Data preserved for future reference</li>
                          {plan === 'ARCHIVE-ONE-TIME' && (
                            <li>6-year retention guarantee from purchase date</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Storage Usage */}
      {(user?.storageUsage || isAdmin) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="card"
        >
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Storage Usage</h3>
              {isAdmin && <span className="text-xs text-gray-500">Admin View</span>}
            </div>
          </div>

          <div className="space-y-4">
            {user?.storageUsage ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Storage */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-blue-700 font-medium">Total Storage</span>
                      <svg
                        className="h-5 w-5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"
                        />
                      </svg>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">
                      {(user.storageUsage.totalBytes / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {(user.storageUsage.totalBytes / 1024).toFixed(0)} KB total
                    </p>
                  </div>

                  {/* Receipt Count */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-green-700 font-medium">Receipts Uploaded</span>
                      <svg
                        className="h-5 w-5 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                        />
                      </svg>
                    </div>
                    <p className="text-2xl font-bold text-green-900">
                      {user.storageUsage.receiptCount}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {user.storageUsage.receiptCount > 0
                        ? `Avg: ${(user.storageUsage.totalBytes / user.storageUsage.receiptCount / 1024).toFixed(0)} KB/receipt`
                        : 'No receipts yet'}
                    </p>
                  </div>

                  {/* Last Updated */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-purple-700 font-medium">Last Upload</span>
                      <svg
                        className="h-5 w-5 text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-sm font-semibold text-purple-900">
                      {user.storageUsage.lastUpdated
                        ? new Date(
                            (user.storageUsage.lastUpdated as any).toDate()
                          ).toLocaleDateString('en-CA', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'N/A'}
                    </p>
                    <p className="text-xs text-purple-600 mt-1">
                      {user.storageUsage.lastUpdated
                        ? new Date(
                            (user.storageUsage.lastUpdated as any).toDate()
                          ).toLocaleTimeString('en-CA', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </p>
                  </div>
                </div>

                {/* Storage Bar */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Storage Quota</span>
                    <span className="text-xs text-gray-500">
                      {((user.storageUsage.totalBytes / (100 * 1024 * 1024)) * 100).toFixed(1)}% of
                      100 MB used
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        (user.storageUsage.totalBytes / (100 * 1024 * 1024)) * 100 < 50
                          ? 'bg-green-500'
                          : (user.storageUsage.totalBytes / (100 * 1024 * 1024)) * 100 < 80
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      }`}
                      style={{
                        width: `${Math.min((user.storageUsage.totalBytes / (100 * 1024 * 1024)) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    💡 Receipts are automatically compressed to ~250KB each to optimize storage.
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125"
                  />
                </svg>
                <h4 className="mt-2 text-sm font-medium text-gray-900">No receipts uploaded yet</h4>
                <p className="mt-1 text-sm text-gray-500">
                  Upload receipt photos when adding expenses to track your storage usage.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Data & Privacy */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Data & Privacy</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Export Your Data</h4>
              <p className="text-sm text-gray-500">
                Download all your expenses, sessions, and reports
              </p>
            </div>
            <button onClick={handleExportData} className="btn-secondary btn-sm">
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Delete Account</h4>
              <p className="text-sm text-gray-500">Permanently delete your account and all data</p>
            </div>
            <button
              onClick={() => {
                // TODO: Implement account deletion functionality
                if (
                  window.confirm(
                    'Are you sure you want to delete your account? This action cannot be undone.'
                  )
                ) {
                  console.log('Account deletion functionality to be implemented');
                }
              }}
              className="btn-danger btn-sm"
            >
              Delete
            </button>
          </div>
        </div>
      </motion.div>

      {/* Sign Out */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Sign Out</h3>
            <p className="text-sm text-gray-500">Sign out of your AutoTaxPilot account</p>
          </div>
          <button onClick={handleSignOut} className="btn-secondary">
            <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
            Sign Out
          </button>
        </div>
      </motion.div>

      {/* Footer */}
      <Footer delay={0.7} />

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        currentPlan={plan}
        preselectedPlan={preselectedPlanForPaywall}
      />
    </div>
  );
};

export default Profile;
